import { useCallback, useEffect, useState } from 'react';
import type { Trip, TripRoute, TripTransition } from '../types/trip';
import type { AlertEvent } from '../types/alert';
import type { LogbookEntry } from '../types/logbook';
import type { Expense } from '../types/expense';
import { getTripRoute, getAllTripAlerts, getTripTransitions, getTripLogbookEntries, getTripExpenses } from '../lib/api/endpoints';
import { tripRouteFromDto, tripTransitionsFromDto, alertFromDto, logbookEntryFromDto, expenseFromDto } from '../lib/api/mappers';

/**
 * FE-11 — el expediente derivado de un viaje: ruta, alertas, cambios del viaje,
 * novedades (desde FE-13) y gastos (desde FE-12).
 *
 * Todos se piden juntos porque salen de la MISMA ventana
 * (ADR-024), y por lo mismo **no se cachean por id de viaje**: corregir el
 * viaje cambia las tres respuestas sin tocar una sola fila de telemetría. El
 * `dependeDe` de abajo es esa regla hecha código — cuando la ventana del viaje
 * cambia, esto se vuelve a pedir solo.
 *
 * Un viaje SIN ventana (cancelado, o el `0001-01-01` del issue #93) no tiene
 * trayecto ni alertas que pedir: no hay nada dentro de una ventana vacía. Se
 * devuelven vacíos sin llamar a la API — dos 200 con listas vacías cuestan lo
 * mismo que no preguntarlos, pero preguntarlos sugiere que la respuesta podría
 * cambiar.
 *
 * ⚠️ Las NOVEDADES y los GASTOS sí se piden siempre, incluso sin ventana, y esa
 * asimetría es deliberada: los dos pueden llevar el `tripId` ESTAMPADO por
 * alguien, y esa declaración manda sobre la ventana (ADR-024). Un viaje
 * cancelado con una varada asignada a mano —o con el tanqueo que se hizo antes
 * de cancelarlo— tiene expediente aunque no tenga ni un metro de trayecto, y es
 * justo el caso en que a alguien le interesa mirarlo.
 */
export function useTripDetail(trip: Trip | null) {
  const [route, setRoute] = useState<TripRoute | null>(null);
  const [alerts, setAlerts] = useState<AlertEvent[]>([]);
  const [transitions, setTransitions] = useState<TripTransition[]>([]);
  const [logbook, setLogbook] = useState<LogbookEntry[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const tripId = trip?.id ?? null;
  // La ventana forma parte de la dependencia a propósito: tras una corrección
  // el id es el mismo y el expediente ya no.
  const dependeDe = trip?.window ? `${trip.window.from}|${trip.window.to}` : '';

  useEffect(() => {
    if (!tripId) {
      setRoute(null);
      setAlerts([]);
      setTransitions([]);
      setLogbook([]);
      setExpenses([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        // Los cambios, las novedades y los gastos existen siempre (un viaje
        // cancelado también se auditó, y puede tener novedades o gastos
        // asignados a mano); la ruta y las alertas solo tienen sentido con una
        // ventana real.
        const [transitionsRes, logbookRes, expensesRes, routeRes, alertsRes] = await Promise.all([
          getTripTransitions(tripId),
          getTripLogbookEntries(tripId),
          getTripExpenses(tripId),
          dependeDe ? getTripRoute(tripId) : Promise.resolve(null),
          dependeDe ? getAllTripAlerts(tripId) : Promise.resolve([]),
        ]);
        if (cancelled) return;
        setTransitions(tripTransitionsFromDto(transitionsRes.items));
        setLogbook(logbookRes.items.map(logbookEntryFromDto));
        setExpenses(expensesRes.items.map(expenseFromDto));
        setRoute(routeRes ? tripRouteFromDto(routeRes) : null);
        setAlerts(alertsRes.map(alertFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setRoute(null);
          setAlerts([]);
          setTransitions([]);
          setLogbook([]);
          setExpenses([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando el viaje'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [tripId, dependeDe, reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  return { route, alerts, transitions, logbook, expenses, isLoading, error, reload };
}
